import { UseQueryResult, useQueries, useQueryClient } from '@tanstack/react-query';

import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { AttachFile, FileDownload, Visibility } from '@mui/icons-material';
import { Alert, Box, Button, IconButton, Link, Paper, Stack, Tooltip, Typography } from '@mui/material';

import { ConversationMessageContentViewModel, FileClient, FileViewModel } from '@@api';
import { useApiClient, useToastService } from '@@common';

import {
	AttachmentPreviewSelection,
	ChatAttachmentPreviewDialog,
	ChatAttachmentThumbnail,
	downloadChatAttachment,
	getAttachmentPreviewKind,
} from '../../components/Chat/ChatAttachmentPreview';
import ChatHtmlFileContent from '../../components/Chat/ChatHtmlFileContent';
import { getChatAttribute } from '../../components/Chat/ChatMessageAttributes';
import { ChatAttachment } from '../../components/Chat/PromptChatAdapter';

const FILE_PREVIEW_CACHE_MS = 60_000;

export default function ConversationAttachments({ contents }: { contents: ConversationMessageContentViewModel[] }) {
	const client = useApiClient(FileClient);
	const queryClient = useQueryClient();
	const toast = useToastService();
	const [selection, setSelection] = useState<AttachmentPreviewSelection>();
	const [downloadingId, setDownloadingId] = useState<string>();
	const files = useMemo(() => {
		const unique = new Map<string, ChatAttachment>();
		for (const content of contents) {
			if (content.fileId && !unique.has(content.fileId)) {
				unique.set(content.fileId, { fileId: content.fileId, fileName: content.fileName || content.fileId });
			}
		}
		return Array.from(unique.values());
	}, [contents]);
	const combineFiles = useCallback(
		(queries: UseQueryResult<FileViewModel>[]) =>
			queries.map((query, index) => ({
				attachment: {
					...files[index],
					fileName: query.data?.name || files[index].fileName,
					contentType: query.data?.contentType,
					size: query.data?.size,
				},
				isLoading: query.isLoading,
				isError: query.isError,
				url: query.data?.url,
				scripts:
					getChatAttribute(contents.find((content) => content.fileId === files[index].fileId)?.attributes, 'ContentType', 'sk:contentType')?.toLowerCase() ===
					'sandboxed-html-v1',
			})),
		[contents, files],
	);
	const fileQueries = useQueries({
		queries: files.map((file) => ({
			queryKey: ['files', 'detail', file.fileId],
			queryFn: ({ signal }: { signal: AbortSignal }) => client.getById(file.fileId, undefined, signal),
			staleTime: FILE_PREVIEW_CACHE_MS,
		})),
		combine: combineFiles,
	});
	const attachments = useMemo(() => fileQueries.map((file) => file.attachment), [fileQueries]);
	const resolveUrl = useCallback(
		async (attachment: ChatAttachment) => {
			const file = await queryClient.fetchQuery({
				queryKey: ['files', 'detail', attachment.fileId],
				queryFn: ({ signal }) => client.getById(attachment.fileId, undefined, signal),
				staleTime: FILE_PREVIEW_CACHE_MS,
			});
			if (!file?.url) throw new Error('No attachment URL available.');
			return file.url;
		},
		[client, queryClient],
	);
	const download = async (attachment: ChatAttachment) => {
		setDownloadingId(attachment.fileId);
		try {
			downloadChatAttachment(await resolveUrl(attachment), attachment.fileName);
		} catch {
			toast.error('Unable to download this attachment.');
		} finally {
			setDownloadingId(undefined);
		}
	};
	const resolveHtml = useCallback(
		async (fileId: string, signal?: AbortSignal) => {
			const url = await resolveUrl({ fileId, fileName: 'attachment.html' });
			const response = await fetch(url, { signal });
			if (!response.ok) throw new Error('Unable to load HTML preview.');
			return response.text();
		},
		[resolveUrl],
	);

	if (!files.length) return null;
	return (
		<>
			<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 1 }}>
				{attachments.map((attachment, index) => {
					const query = fileQueries[index];
					const preview = () => setSelection({ attachment, attachments });
					const previewKind = getAttachmentPreviewKind(attachment);
					const isHtml = attachment.contentType?.split(';', 1)[0].trim().toLowerCase() === 'text/html' || /\.html?$/i.test(attachment.fileName);
					const hasInlinePreview = isHtml || previewKind === 'pdf' || previewKind === 'text';
					return (
						<Paper
							key={attachment.fileId}
							variant="outlined"
							sx={{
								p: 1,
								minWidth: 0,
								maxWidth: hasInlinePreview ? undefined : 240,
								gridColumn: hasInlinePreview ? '1 / -1' : undefined,
								borderRadius: 1.5,
								bgcolor: 'background.default',
							}}
						>
							<Stack spacing={0.75}>
								{previewKind === 'image' ? (
									<Box sx={{ '& > button': { width: '100%' } }}>
										<ChatAttachmentThumbnail attachment={attachment} resolveUrl={resolveUrl} onClick={preview} />
									</Box>
								) : !hasInlinePreview ? (
									<Box sx={{ display: 'grid', placeItems: 'center', height: 64, color: 'text.secondary' }}>
										<AttachFile sx={{ fontSize: 32 }} />
									</Box>
								) : null}
								<Link component={RouterLink} to={`/file/${attachment.fileId}`} variant="body2" title={attachment.fileName} sx={{ overflowWrap: 'anywhere' }}>
									{attachment.fileName}
								</Link>
								<Typography variant="caption" color="text.secondary">
									{attachment.contentType ?? 'File'}
									{attachment.size !== undefined ? ` · ${Math.max(1, Math.ceil(attachment.size / 1024))} KB` : ''}
								</Typography>
								{query.isError && (
									<Alert severity="warning" sx={{ p: 0.5, fontSize: '0.75rem' }}>
										Preview unavailable. Check file access or expiration.
									</Alert>
								)}
								{isHtml ? (
									<ChatHtmlFileContent
										fileId={attachment.fileId}
										fileName={attachment.fileName}
										scripts={query.scripts}
										resolveHtml={resolveHtml}
										resolveAttachmentUrl={resolveUrl}
									/>
								) : (
									<>
										{hasInlinePreview && query.url && (
											<Box
												component="iframe"
												src={query.url}
												title={`${attachment.fileName} inline preview`}
												loading="lazy"
												sandbox=""
												referrerPolicy="no-referrer"
												sx={{ width: '100%', height: 280, border: 0, borderRadius: 1, bgcolor: 'common.white' }}
											/>
										)}
										<Stack direction="row" alignItems="center" justifyContent="space-between">
											<Button size="small" startIcon={<Visibility fontSize="small" />} onClick={preview} disabled={query.isLoading || !query.url}>
												Preview
											</Button>
											<Tooltip title={`Download ${attachment.fileName}`}>
												<span>
													<IconButton
														size="small"
														aria-label={`Download ${attachment.fileName}`}
														disabled={!query.url || downloadingId !== undefined}
														onClick={() => void download(attachment)}
													>
														<FileDownload fontSize="small" />
													</IconButton>
												</span>
											</Tooltip>
										</Stack>
									</>
								)}
							</Stack>
						</Paper>
					);
				})}
			</Box>
			{selection && <ChatAttachmentPreviewDialog selection={selection} resolveUrl={resolveUrl} onClose={() => setSelection(undefined)} />}
		</>
	);
}
